import { AntragObj } from '../../api/models/Antrag/AntragsModel';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../api/beh-api';
import { useNotification } from '../notification/useNotification';
import {
  conflictErrorMsgWithReloadHint,
  ErrorType
} from '../utils/Error/ErrorCode';

interface AssignmentProps {
  antragObj: AntragObj | CaseDetailsModel;
}

interface UpdateCasePatch {
  caseVersion: string;
  bearbeiterId: string | null;
  bearbeitungsstand: string;
}

function useAssignment(props: AssignmentProps) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const queryClient = useQueryClient();
  const { mutateAsync: updateCase, isPending } = useMutation({
    mutationFn: ({
      caseId,
      updatedData
    }: {
      caseId: string;
      updatedData: UpdateCasePatch;
    }) =>
      notifyUserFromRequest(
        () => behClient.patch(BehApiRoutes.case(caseId), updatedData),
        {
          successMessage: 'Antrag wurde erfolgreich aktualisiert!',
          errorMessage: 'Antrag konnte nicht aktualisiert werden',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_UPDATE_NOT_ALLOWED]: () => {
              notifyUser({
                intent: 'error',
                title: conflictErrorMsgWithReloadHint
              });
            },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_USER_LOGGED_IN]: () => {
              notifyUser({
                intent: 'error',
                title:
                  'Die Benutzer:in ist aktuell eingeloggt, es konnte keine Zuweisung vorgenommen werden'
              });
            }
          }
        }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey.at(0);
          if (typeof queryKey === 'string') {
            return (
              queryKey.includes(`${BehApiRoutes.allCases()}?page=`) ||
              queryKey.includes(`${BehApiRoutes.case(variables.caseId)}`)
            );
          }
          return false;
        }
      });
    }
  });

  const assignCase = (bearbeiterid: string, version: string) => {
    updateCase({
      caseId: props.antragObj.caseId,
      updatedData: {
        caseVersion: version,
        bearbeiterId: bearbeiterid,
        bearbeitungsstand: 'In Bearbeitung'
      }
    }).catch(() => {});
  };

  const revokeCase = (version: string) => {
    updateCase({
      caseId: props.antragObj.caseId,
      updatedData: {
        caseVersion: version,
        bearbeiterId: null,
        bearbeitungsstand: 'Offen'
      }
    }).catch(() => {});
  };

  return { assignCase, revokeCase, isPending };
}

export default useAssignment;
